import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import React, { useEffect, useId, useState } from 'react';

import { cn } from '../helpers/cn';
import { IconDown16 } from '../icons/ic-down';
import ErrorLabel from '../label/ErrorLabel';
import InputLabel from '../label/InputLabel';

import { DROPDOWN_PANEL_ANCHOR_CLASSNAME } from './dropdown-anchor';
import {
    DROPDOWN_OPTION_CLASSNAME,
    DROPDOWN_PANEL_CLASSNAME,
    dropdownTriggerClassName,
    type DropdownTriggerVariant
} from './dropdown-colors';
import { DROPDOWN_SCROLLBAR_CLASSNAME } from './dropdown-scrollbar';

export type BasicDropdownOption<TValue = string | number> = {
    value: TValue;
    label?: string | React.JSX.Element;
    isSelectable?: boolean;
};

export type BasicDropdownProps<TValue = string | number> = {
    className?: string;
    itemClassName?: string;
    /** Extra classes for the trigger button (e.g. `w-full` to make it fill the container instead of fitting the selected option). */
    buttonClassName?: string;
    /** What is behind the trigger; defaults to `tint`. See `DropdownTriggerVariant`. */
    triggerVariant?: DropdownTriggerVariant;
    height?: 'default-48' | 'custom';
    label?: string;
    placeholder?: string | React.JSX.Element;
    preselectedIndex?: number;
    options: BasicDropdownOption<TValue>[];
    onChange?: (selectedItem: BasicDropdownOption<TValue>, index: number) => boolean | void;
    onClose?: () => void;
    triggerIcon?: React.JSX.Element;
    customIcon?: React.JSX.Element;
    disabled?: boolean;
    isDropdownAbsolutePositioned?: boolean;
    error?: string | null;
    openingDirection?: 'up' | 'down';
    required?: boolean;
    paginationRef?: (node?: Element | null) => void;
};

/**
 * Helper component to observe Listbox open state and fire onClose.
 * Replicates the original useEffect([isDropdownOpen]) behavior.
 */
function CloseObserver({ open, onClose }: { open: boolean; onClose?: () => void }) {
    useEffect(() => {
        if (!open && onClose) {
            onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    return null;
}

function BasicDropdown<TValue>(props: BasicDropdownProps<TValue>): React.JSX.Element {
    const {
        className,
        itemClassName,
        buttonClassName,
        triggerVariant,
        height,
        label,
        preselectedIndex,
        options,
        onChange,
        onClose,
        triggerIcon,
        customIcon,
        disabled,
        isDropdownAbsolutePositioned,
        placeholder,
        error,
        openingDirection,
        paginationRef
    } = props;

    const [selectedOption, setSelectedOption] = useState<number>(
        placeholder && preselectedIndex === undefined ? -1 : preselectedIndex ? preselectedIndex : 0
    );
    const [focused, setFocused] = useState(false);

    const buttonId = useId();
    const errorId = `${buttonId}-error`;

    // Update the current selected option if the preselectedIndex changes.
    useEffect(() => {
        setSelectedOption(placeholder && preselectedIndex === undefined ? -1 : preselectedIndex ? preselectedIndex : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preselectedIndex]);

    // The label sits OUTSIDE the control, on whatever the dropdown was dropped into — a dark app
    // page or a white modal — so it cannot take a colour from the dropdown's own theme variables the
    // way the trigger and panel do. It inherits the surrounding text colour instead and is dimmed
    // when unfocused, which is correct in both contexts; the four removed variants each hardcoded a
    // colour and so each got one of the two contexts wrong.
    const labelColor = 'opacity-50';
    const focusedLabelColor = 'opacity-100';

    const triggerClassName = cn(dropdownTriggerClassName(triggerVariant), 'active:opacity-80');
    const optionClassName = cn(DROPDOWN_OPTION_CLASSNAME, 'text-body-14 font-medium active:opacity-80');

    function enterFocus() {
        if (disabled !== true) {
            setFocused(true);
        }
    }

    function exitFocus() {
        setFocused(false);
    }

    function onChangeInternal(index: number) {
        const option = options[index];
        if (onChange) {
            const apply = onChange(option, index);
            if (apply !== false) {
                setSelectedOption(index);
            }
        } else {
            setSelectedOption(index);
        }
    }

    const selectedLabel =
        selectedOption === -1 ? (
            <span>{placeholder}</span>
        ) : (
            <span>
                {options?.[selectedOption]?.isSelectable !== false && (options?.[selectedOption]?.label ?? '-')}
            </span>
        );

    const useAnchor = isDropdownAbsolutePositioned !== false;
    const anchorPosition = openingDirection === 'up' ? 'top start' : 'bottom start';

    function renderOptions() {
        return options?.map((it, index) => (
            <ListboxOption
                key={`dropdownItem#${index}`}
                value={index}
                disabled={it.isSelectable === false}
                className={cn(
                    // Fills the panel rather than hugging its own text, so hover and selection
                    // highlight the whole row and short options are not narrower than long ones.
                    'w-full active:opacity-80',
                    {
                        // Minimum (not fixed) row height: keeps the compact ~36px rhythm for
                        // single-line options, while a label too long for the panel width grows
                        // its own row instead of spilling over the neighbouring ones.
                        'min-h-36 px-12 py-6 flex items-center break-words': !React.isValidElement(it.label),
                        'cursor-pointer': it.isSelectable !== false
                    },
                    optionClassName,
                    itemClassName
                )}>
                {it.label ?? '-'}
            </ListboxOption>
        ));
    }

    return (
        <div
            className={cn(className, 'w-full flex flex-col')}
            onFocus={enterFocus}
            onBlur={exitFocus}>
            {label && (
                <InputLabel
                    label={label}
                    htmlFor={buttonId}
                    labelStyle={focused ? focusedLabelColor : labelColor}
                    requiredLabel={props.required}
                />
            )}
            <Listbox
                as="div"
                value={selectedOption}
                onChange={onChangeInternal}
                disabled={disabled}>
                {({ open }) => (
                    <>
                        <CloseObserver
                            open={open}
                            onClose={onClose}
                        />
                        <ListboxButton
                            id={buttonId}
                            aria-invalid={!!error}
                            aria-describedby={error ? errorId : undefined}
                            className={cn(
                                'flex flex-row items-center justify-between select-none space-x-12',
                                // One `transition` shorthand, not `transition-colors` plus
                                // `transition-opacity`: both set `transition-property`, so the
                                // second silently won and the trigger's `active:opacity-80` was the
                                // only thing that ever animated.
                                'transition duration-100',
                                'rounded-4',
                                buttonClassName,
                                triggerClassName,
                                {
                                    'h-48': height !== 'custom',
                                    'h-full': height === 'custom',
                                    'px-12 py-6': !(
                                        options?.[selectedOption]?.label &&
                                        React.isValidElement(options[selectedOption].label)
                                    ),
                                    'opacity-50 cursor-not-allowed': disabled
                                }
                            )}>
                            {triggerIcon ? (
                                <div className="flex flex-row items-center space-x-6">
                                    {triggerIcon}
                                    {selectedLabel}
                                </div>
                            ) : (
                                selectedLabel
                            )}
                            {customIcon || (
                                <div>
                                    <IconDown16
                                        className={cn('transition-all duration-100 flex-none', {
                                            'mr-12':
                                                options?.[selectedOption]?.label &&
                                                React.isValidElement(options[selectedOption].label),
                                            'rotate-180': open
                                        })}
                                    />
                                </div>
                            )}
                        </ListboxButton>
                        {useAnchor ? (
                            <ListboxOptions
                                anchor={anchorPosition}
                                transition
                                // Headless UI treats an open `ListboxOptions` as modal by default:
                                // it scroll-locks the page and marks everything outside the panel
                                // `inert`. On a dialog that is right; on a select it means opening
                                // the footer language picker froze the page's scrolling and blanked
                                // the fixed InfoBanner, because the banner sits outside the panel
                                // and got inerted along with the rest of the document.
                                modal={false}
                                className={cn(
                                    // `z-modal-view-overlay` (9100), not the previous arbitrary
                                    // z-[900]: headlessui portals this list to document.body, and
                                    // anything opened from inside a full-screen surface at
                                    // `z-modal-view` (9000) would otherwise render behind it — the
                                    // dropdown opens correctly and is simply invisible, which reads
                                    // as "the control does nothing".
                                    // `min-w` rather than a fixed `w`: the panel is at least as wide
                                    // as its button, but a label longer than a narrow button may
                                    // grow it — up to 480px, beyond which lines wrap instead of the
                                    // panel taking over the screen. (A button wider than 480px still
                                    // wins, since min-width beats max-width.)
                                    // The `!` is load-bearing: `anchor` makes Headless UI write
                                    // `max-width: <space available>px` INLINE, which a normal class
                                    // cannot outrank — only an important one can.
                                    'min-w-[var(--button-width)] !max-w-[480px] rounded-4',
                                    // No overflow or height of our own here. `anchor` also makes
                                    // Headless UI set `overflow: auto` and
                                    // `max-height: min(var(--anchor-max-height, 100vh), <space below
                                    // the button>)` inline, so THIS element is already the scroll
                                    // container. A second, fixed-height scroll box nested inside it
                                    // simply reached its limit first and scrolled instead — which is
                                    // how lists with ample room to open in full still ended up with
                                    // a scrollbar. `--anchor-max-height` is the supported way to cap
                                    // the panel, and unlike a `max-h` class it still yields to the
                                    // viewport when there is less room than the cap.
                                    '[--anchor-max-height:320px]',
                                    DROPDOWN_SCROLLBAR_CLASSNAME,
                                    DROPDOWN_PANEL_ANCHOR_CLASSNAME,
                                    'z-modal-view-overlay',
                                    DROPDOWN_PANEL_CLASSNAME
                                )}>
                                {renderOptions()}
                                {paginationRef && <div ref={paginationRef} />}
                            </ListboxOptions>
                        ) : (
                            <ListboxOptions
                                // Same reason as the anchored branch above.
                                modal={false}
                                className={cn(
                                    'w-full rounded-4',
                                    // The opt-out (`isDropdownAbsolutePositioned={false}`) renders
                                    // in flow with no `anchor`, so Headless UI contributes no inline
                                    // sizing and this panel has to bound itself. Explicit axes,
                                    // never the `overflow-auto` shorthand: the list only ever
                                    // scrolls vertically, and a horizontal scrollbar would mean a
                                    // row wider than the panel, which `w-full` on each row (see
                                    // `renderOptions`) already rules out.
                                    'max-h-[320px] overflow-y-auto overflow-x-hidden',
                                    DROPDOWN_SCROLLBAR_CLASSNAME,
                                    DROPDOWN_PANEL_CLASSNAME
                                )}>
                                {renderOptions()}
                                {paginationRef && <div ref={paginationRef} />}
                            </ListboxOptions>
                        )}
                    </>
                )}
            </Listbox>
            {error && (
                <ErrorLabel
                    error={error}
                    id={errorId}
                />
            )}
        </div>
    );
}

export default BasicDropdown;
